import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Printer, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { printLabels } from "@/lib/printLabels";

// The core label (on each roll) and the shipper label (on each box sent to the customer), printed from the Dispatch
// stage. Everything a label says fills in from the earlier stages (routes/labels.py); what they never recorded is
// typed here, saved with the stage as its ship_* fields, and always wins over the filled-in figure.
const COMPANY = { name: "MedPack India Pvt Ltd", address: ["Sy. No. 225, Bahadurpally, Dundigal Mandal,", "Medchal-Malkajgiri, Telangana-500043"] };
const FONT = { fontFamily: "Arial, Helvetica, sans-serif", color: "#000" };
const MAX = 1000;

// The stickers the floor uses for a shipper label: 100 x 150 mm (4 x 6 in) first. Any other size is a
// custom width x height in mm.
const SIZES = [
  { id: "100x150", label: "100 × 150 mm (4 × 6 in)", w: 100, h: 150 },
  { id: "100x100", label: "100 × 100 mm", w: 100, h: 100 },
  { id: "100x75", label: "100 × 75 mm", w: 100, h: 75 },
  { id: "100x50", label: "100 × 50 mm", w: 100, h: 50 },
  { id: "75x50", label: "75 × 50 mm", w: 75, h: 50 },
  { id: "a6", label: "A6 · 105 × 148 mm", w: 105, h: 148 },
  { id: "a5", label: "A5 · 148 × 210 mm", w: 148, h: 210 },
];
// The core label is short (a few lines + a small QR): its stock should match that, not the tall shipper
// sticker, so no blank label is fed through the printer under it. "fit" is exactly the label's own content
// box (CoreLabel's refW x refH below), so nothing is stretched or left blank.
const CORE_SIZES = [
  { id: "fit", label: "Fit the label (100 × 50 mm)", w: 100, h: 50 },
  { id: "100x40", label: "100 × 40 mm", w: 100, h: 40 },
  { id: "75x50", label: "75 × 50 mm", w: 75, h: 50 },
  { id: "75x40", label: "75 × 40 mm", w: 75, h: 40 },
  { id: "100x75", label: "100 × 75 mm", w: 100, h: 75 },
];
const clampMm = (v) => Math.min(400, Math.max(20, Number(v) || 100));
const sizeMm = (s, list = SIZES) => (s.id === "custom" ? { w: clampMm(s.w), h: clampMm(s.h) } : list.find((x) => x.id === s.id) || list[0]);

/** The size picked for a kind of label, kept on this device: the sticker stock is a printer setting, not job data. */
function useLabelSize(kind, defaultId) {
  const key = `erp.labelSize.${kind}`;
  const [size, setSize] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key)) || { id: defaultId }; } catch { return { id: defaultId }; }
  });
  const set = (next) => {
    setSize(next);
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* storage unavailable */ }
  };
  return [size, set];
}

const has = (v) => v !== undefined && v !== null && String(v).trim() !== "";
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
const plain = (n) => String(Math.round(n * 1000) / 1000);
const day = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");
const kgText = (v) => { const n = num(v); return n === null ? "" : `${n.toFixed(3)} kg`; };
/** The server's QR (an SVG at its own mm size) as one that fills whatever box it is put in. */
const fitSvg = (svg) => String(svg || "").replace(/<\?xml[^>]*\?>/, "")
  .replace(/<svg[^>]*>/, (tag) => tag.replace(/\s(width|height)="[^"]*"/g, "").replace("<svg", '<svg width="100%" height="100%"'));

/** What each label says: the server's filled-in figures, with whatever was typed at Dispatch over them. */
function useEffective(info, data) {
  return useMemo(() => {
    if (!info) return null;
    const ov = Array.isArray(data.ship_boxes) ? data.ship_boxes : [];
    const total = Math.max(info.boxes.length, ov.length);
    const boxes = Array.from({ length: total }, (_, i) => {
      const d = info.boxes[i] || {};
      const o = ov[i] || {};
      return {
        no: i + 1,
        qty_text: has(o.qty) ? String(o.qty) : d.qty_text || "",
        auto_qty: d.qty_text || "",
        qty_hint: d.qty_hint ?? null,
        gross: has(o.gross) ? String(o.gross) : d.gross != null ? plain(d.gross) : "",
        hint: d.gross_hint ?? null,
      };
    });
    const typed = Array.isArray(data.ship_core_rows) && data.ship_core_rows.length;
    return {
      work_no: info.mpwo_number,
      customer_name: info.customer_name,
      item_code: info.item_code,
      po_number: info.po_number,
      po_date: info.po_date,
      item_name: has(data.ship_item_name) ? data.ship_item_name : info.item_name,
      mfg_date: has(data.ship_mfg_date) ? String(data.ship_mfg_date).slice(0, 10) : info.mfg_date,
      packed_by: has(data.ship_packed_by) ? data.ship_packed_by : info.packed_by,
      checked_by: has(data.ship_checked_by) ? data.ship_checked_by : info.checked_by,
      boxes,
      groups: typed ? data.ship_core_rows : info.core.groups,
      groups_typed: !!typed,
      core_source: info.core.source,
      boxes_source: info.boxes_source,
    };
  }, [info, data.ship_boxes, data.ship_core_rows, data.ship_item_name, data.ship_mfg_date, data.ship_packed_by, data.ship_checked_by]);
}

/** One label per physical roll: each group is `rolls` rolls with `labels` on each. */
const rollList = (groups) => groups.flatMap((g) => {
  const labels = num(g.labels);
  const rolls = Math.min(Math.floor(num(g.rolls) || 0), MAX);
  return labels && labels > 0 && rolls > 0 ? Array.from({ length: rolls }, () => labels) : [];
}).slice(0, MAX);

// ---- the labels themselves: laid out in mm at a reference size, then scaled to fit the chosen sheet ---------------
function Fit({ w, h, refW, refH, children }) {
  const s = Math.min(w / refW, h / refH);
  return (
    <div style={{ width: `${w}mm`, height: `${h}mm`, position: "relative", overflow: "hidden", background: "#fff" }}>
      <div style={{ position: "absolute", top: 0, left: `${(w - refW * s) / 2}mm`, width: `${refW}mm`, height: `${refH}mm`, transform: `scale(${s})`, transformOrigin: "top left" }}>
        {children}
      </div>
    </div>
  );
}

function CoreLabel({ e, labels, rollNo, totalRolls, w, h, qr }) {
  const line = { fontSize: "2.8mm", fontWeight: 700, lineHeight: 1.3, whiteSpace: "nowrap" };
  return (
    <Fit w={w} h={h} refW={100} refH={50}>
      <div data-testid="core-label" style={{ ...FONT, width: "100mm", height: "50mm", boxSizing: "border-box", padding: "3mm 4mm", display: "flex", flexDirection: "column" }}>
        <div style={{ textAlign: "center", fontSize: "3.4mm", fontWeight: 700, letterSpacing: "0.3mm" }}>MEDPACK INDIA PVT LTD</div>
        <div style={{ marginTop: "1.8mm", fontSize: "2.6mm", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}><b>Item Name :</b> {e.item_name}</div>
        <div style={{ marginTop: "1.6mm", display: "flex", gap: "4mm" }}>
          <span style={line}>Item Code : {e.item_code}</span>
          <span style={line}>Qty/Roll : {plain(labels)} lbl</span>
        </div>
        <div style={{ marginTop: "1.6mm", display: "flex", gap: "4mm" }}>
          <span style={line}>Wno : {e.work_no}</span>
          {rollNo != null && <span style={line}>Roll No : {rollNo}{totalRolls ? `/${totalRolls}` : ""}</span>}
        </div>
        {/* Same pattern as ShipperLabel's QR below: a flex spacer, then the QR as plain inline SVG — that
            combination is the one that's actually proven to print correctly across many sheets. */}
        <div style={{ flex: 1 }} />
        {qr && (
          <div style={{ textAlign: "center" }}>
            <div data-testid="core-label-qr" style={{ width: "20mm", height: "20mm", display: "inline-block" }} dangerouslySetInnerHTML={{ __html: qr }} />
          </div>
        )}
      </div>
    </Fit>
  );
}

function ShipperLabel({ e, box, total, qr, w, h }) {
  // field names all one size on one line; the key rows (code, work number, shipper number) have bigger values
  const row = (k, v, big) => (
    <div style={{ display: "flex", alignItems: "baseline", gap: "1.5mm", marginBottom: "1.7mm", lineHeight: 1.25 }}>
      <span style={{ width: "39mm", flexShrink: 0, whiteSpace: "nowrap", fontSize: "3.8mm", fontWeight: 700 }}>{k} :</span>
      <span style={{ fontSize: big ? "4.8mm" : "3.7mm", fontWeight: big ? 700 : 400, overflowWrap: "anywhere", minWidth: 0 }}>{v}</span>
    </div>
  );
  return (
    <Fit w={w} h={h} refW={100} refH={150}>
      <div data-testid="shipper-label" style={{ ...FONT, width: "100mm", height: "150mm", boxSizing: "border-box", padding: "5mm 5mm 4mm", display: "flex", flexDirection: "column" }}>
        <div style={{ textAlign: "center", borderBottom: "0.4mm solid #000", paddingBottom: "2mm" }}>
          <div style={{ fontSize: "5.4mm", fontWeight: 700 }}>{COMPANY.name}</div>
          {COMPANY.address.map((l) => <div key={l} style={{ fontSize: "3.3mm", lineHeight: 1.3 }}>{l}</div>)}
        </div>
        <div style={{ marginTop: "3.2mm" }}>
          {row("Customer Name", e.customer_name.toUpperCase())}
          {row("Item Name", e.item_name)}
          {row("Item Code", e.item_code, true)}
          {row("PO No.& Date", [e.po_number, day(e.po_date)].filter(Boolean).join(" & "))}
          {row("WO No./Batch No", e.work_no, true)}
          {row("Qty Per Box", box.qty_text)}
          {row("Gross Weight", kgText(box.gross))}
          {row("Mfg Date", day(e.mfg_date))}
          {row("Shipper No", `${box.no}/${total}`, true)}
          {row("Packed By", e.packed_by)}
          {row("CHECKED By", e.checked_by)}
        </div>
        <div style={{ flex: 1 }} />
        {qr && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div data-testid="label-qr" style={{ width: "26mm", height: "26mm" }} dangerouslySetInnerHTML={{ __html: qr }} />
            <div style={{ fontSize: "2.6mm", marginTop: "0.8mm" }}>Scan for this job's full record</div>
          </div>
        )}
      </div>
    </Fit>
  );
}

// ---- the panel -------------------------------------------------------------------------------------------------
function SizePicker({ value, onChange, testId, sizes = SIZES }) {
  const custom = value.id === "custom";
  const box = "h-8 rounded-sm border border-neutral-300 bg-white px-2 text-xs";
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-neutral-600">Sticker size</span>
      <select value={value.id} data-testid={testId} className={box}
        onChange={(ev) => onChange(ev.target.value === "custom" ? { id: "custom", w: sizeMm(value, sizes).w, h: sizeMm(value, sizes).h } : { id: ev.target.value })}>
        {sizes.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        <option value="custom">Custom size…</option>
      </select>
      {custom && (
        <>
          <Input type="number" min="20" max="400" value={value.w ?? ""} onChange={(ev) => onChange({ ...value, w: ev.target.value })} className="h-8 w-20 text-xs" aria-label="Width in mm" />
          <span className="text-xs text-neutral-500">×</span>
          <Input type="number" min="20" max="400" value={value.h ?? ""} onChange={(ev) => onChange({ ...value, h: ev.target.value })} className="h-8 w-20 text-xs" aria-label="Height in mm" />
          <span className="text-xs text-neutral-500">mm</span>
        </>
      )}
    </div>
  );
}

function RangePicker({ total, value, onChange, noun }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-neutral-600">
      Print {noun}
      <Input type="number" min="1" max={total} value={value.from} placeholder="1" onChange={(ev) => onChange({ ...value, from: ev.target.value })} className="h-8 w-16 text-xs" aria-label={`First ${noun}`} />
      to
      <Input type="number" min="1" max={total} value={value.to} placeholder={String(total)} onChange={(ev) => onChange({ ...value, to: ev.target.value })} className="h-8 w-16 text-xs" aria-label={`Last ${noun}`} />
      <span className="text-neutral-400">of {total}</span>
    </div>
  );
}

const slice = (list, r) => {
  const from = Math.max(1, Math.floor(num(r.from) || 1));
  const to = Math.min(list.length, Math.floor(num(r.to) || list.length));
  return list.slice(from - 1, Math.max(from, to));
};

export default function DispatchLabels({ jobId, data, setMany, readOnly }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [qr, setQr] = useState("");
  const [qrFailed, setQrFailed] = useState(false);
  const [coreSize, setCoreSize] = useLabelSize("core", "fit");
  const [shipSize, setShipSize] = useLabelSize("shipper", "100x150");
  const [coreRange, setCoreRange] = useState({ from: "", to: "" });
  const [shipRange, setShipRange] = useState({ from: "", to: "" });
  const [allGross, setAllGross] = useState("");
  const [run, setRun] = useState(null); // {kind, size, items}: the labels being handed to the printer
  const printRef = useRef(null);
  const e = useEffective(info, data);

  const loadQr = useCallback(() => {
    setQrFailed(false);
    return api.get(`/jobs/${jobId}/qr`, { params: { stage: "dispatch", v: 2 }, responseType: "text", transformResponse: (r) => r })
      .then(({ data: svg }) => setQr(fitSvg(svg)))
      .catch(() => setQrFailed(true));
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return undefined;
    let off = false;
    api.get(`/jobs/${jobId}/labels`).then(({ data: d }) => !off && setInfo(d)).catch((err) => !off && setError(formatApiError(err)));
    loadQr();
    return () => { off = true; };
  }, [jobId, loadQr]);

  // The labels are drawn off screen only for the moment of printing, then copied out by printLabels.
  useEffect(() => {
    if (!run) return;
    printLabels(Array.from(printRef.current?.children || []), { widthMm: run.size.w, heightMm: run.size.h });
    setRun(null);
  }, [run]);

  if (error) return <div className="border border-neutral-200 rounded-sm p-3 text-xs text-neutral-500" data-testid="dispatch-labels">Labels aren't available here: {error}</div>;
  if (!e) return <div className="border border-neutral-200 rounded-sm p-3 text-xs text-neutral-500" data-testid="dispatch-labels">Loading the labels…</div>;

  const rolls = rollList(e.groups);
  const coreFrom = Math.max(1, Math.floor(num(coreRange.from) || 1));
  const coreSet = slice(rolls, coreRange);
  const coreItems = coreSet.map((labels, i) => ({ labels, rollNo: coreFrom + i, totalRolls: rolls.length }));
  const boxSet = slice(e.boxes, shipRange);
  const core = sizeMm(coreSize, CORE_SIZES);
  const ship = sizeMm(shipSize, SIZES);

  // ship_boxes holds one {qty, gross} per box (typed values only; blank means "as filled in")
  const typedBoxes = () => Array.from({ length: e.boxes.length }, (_, i) => ({ qty: "", gross: "", ...((Array.isArray(data.ship_boxes) ? data.ship_boxes : [])[i] || {}) }));
  const setBox = (i, patch) => { const next = typedBoxes(); next[i] = { ...next[i], ...patch }; setMany({ ship_boxes: next }); };
  const addBox = () => setMany({ ship_boxes: [...typedBoxes(), { qty: "", gross: "" }] });
  const extra = (Array.isArray(data.ship_boxes) ? data.ship_boxes.length : 0) > info.boxes.length;
  const removeBox = () => setMany({ ship_boxes: typedBoxes().slice(0, -1) });
  const applyGross = (v) => setMany({ ship_boxes: typedBoxes().map((b) => ({ ...b, gross: v })) });
  const useHints = () => setMany({ ship_boxes: typedBoxes().map((b, i) => (!has(b.gross) && !e.boxes[i].gross && e.boxes[i].hint != null ? { ...b, gross: plain(e.boxes[i].hint) } : b)) });
  const hintsLeft = e.boxes.filter((b) => !b.gross && b.hint != null).length;
  // an even share of what was packed / sent, for boxes the earlier stages gave no figure: offered, never printed unasked
  const qtyOf = (b) => `${plain(b.qty_hint)} Labels`;
  const qtyHintsLeft = e.boxes.filter((b) => !b.qty_text && b.qty_hint != null).length;
  const useQtyHints = () => setMany({ ship_boxes: typedBoxes().map((b, i) => (!e.boxes[i].qty_text && e.boxes[i].qty_hint != null ? { ...b, qty: qtyOf(e.boxes[i]) } : b)) });

  const setGroup = (i, patch) => setMany({ ship_core_rows: e.groups.map((g, k) => (k === i ? { ...g, ...patch } : g)) });
  const addGroup = () => setMany({ ship_core_rows: [...e.groups, { labels: "", rolls: "" }] });
  const removeGroup = (i) => setMany({ ship_core_rows: e.groups.filter((_, k) => k !== i) });

  const start = (kind, size, items) => {
    if (!items.length) { toast.error(kind === "core" ? "No rolls to print: give the quantity per roll and how many rolls." : "No boxes to print."); return; }
    if ((kind === "shipper" || kind === "core") && !qr) {  // never print a label without its QR — every label needs one
      if (qrFailed) { toast.error("The QR failed to load: retrying — try Print again in a moment."); loadQr(); }
      else toast.error("The QR is still loading: try again in a moment.");
      return;
    }
    if (items.length > 200 && !window.confirm(`Print ${items.length} labels?`)) return;
    setRun({ kind, size, items });
  };
  const readOnlyHint = readOnly ? " (this stage is completed: only an admin can change it)" : "";
  const field = "h-8 text-sm";

  return (
    <div className="space-y-5" data-testid="dispatch-labels">
      {/* ---------------- core labels ---------------- */}
      <section className="border border-neutral-200 rounded-sm p-3 space-y-3" data-testid="core-labels">
        <div>
          <div className="label-caps">Core labels · one for each roll</div>
          <div className="text-[11px] text-neutral-500 mt-0.5">Filled in from {e.core_source === "none" ? "nothing yet" : e.core_source}. Change a figure here if the rolls were packed differently{readOnlyHint}.</div>
        </div>
        <div className="space-y-1.5">
          {e.groups.length === 0 && <div className="text-xs text-amber-700">No roll figures found: add how many labels are on each roll and how many rolls.</div>}
          {e.groups.map((g, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`core-group-${i}`}>
              <Input type="number" min="1" value={g.labels ?? ""} disabled={readOnly} onChange={(ev) => setGroup(i, { labels: ev.target.value })} className={`${field} w-28`} aria-label="Labels on each roll" />
              <span className="text-xs text-neutral-500">labels on each of</span>
              <Input type="number" min="1" value={g.rolls ?? ""} disabled={readOnly} onChange={(ev) => setGroup(i, { rolls: ev.target.value })} className={`${field} w-20`} aria-label="Number of rolls" />
              <span className="text-xs text-neutral-500">rolls</span>
              {!readOnly && <button type="button" onClick={() => removeGroup(i)} className="text-neutral-400 hover:text-rose-600" aria-label="Remove this group"><Trash2 size={14} /></button>}
            </div>
          ))}
          {!readOnly && (
            <div className="flex items-center gap-3">
              <button type="button" onClick={addGroup} className="inline-flex items-center gap-1 text-xs text-[#FF5A00] hover:underline" data-testid="core-add-group"><Plus size={12} /> Add a group of rolls</button>
              {e.groups_typed && <button type="button" onClick={() => setMany({ ship_core_rows: [] })} className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:underline"><Undo2 size={12} /> Back to the Slitting figures</button>}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <SizePicker value={coreSize} onChange={setCoreSize} testId="core-size" sizes={CORE_SIZES} />
          <RangePicker total={rolls.length} value={coreRange} onChange={setCoreRange} noun="roll" />
          <Button type="button" onClick={() => start("core", core, coreItems)} data-testid="core-print" className="h-8 rounded-sm bg-[#FF5A00] hover:bg-[#e65100] text-white">
            <Printer size={14} className="mr-1.5" /> Print {coreSet.length} core label{coreSet.length === 1 ? "" : "s"}
          </Button>
        </div>
        {rolls.length > 0 && (
          <div className="overflow-auto border border-dashed border-neutral-300 bg-neutral-50 p-2" data-testid="core-preview">
            <div style={{ zoom: 0.62, width: "fit-content" }}><CoreLabel e={e} labels={rolls[0]} rollNo={1} totalRolls={rolls.length} w={core.w} h={core.h} qr={qr} /></div>
          </div>
        )}
        <div className="text-[11px] text-neutral-500">The small QR on each core label opens this job's full record, same as the shipper label.</div>
      </section>

      {/* ---------------- shipper labels ---------------- */}
      <section className="border border-neutral-200 rounded-sm p-3 space-y-3" data-testid="shipper-labels">
        <div>
          <div className="label-caps">Shipper labels · one for each box sent</div>
          <div className="text-[11px] text-neutral-500 mt-0.5">
            Boxes and quantities from {e.boxes_source === "none" ? "nothing yet" : e.boxes_source}; gross weight from the Packing Slip. Type over any figure to correct it{readOnlyHint}.
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <label>Item name on the label
            <Input value={data.ship_item_name ?? ""} placeholder={e.item_name} disabled={readOnly} onChange={(ev) => setMany({ ship_item_name: ev.target.value })} className={`${field} mt-1`} data-testid="ship-item-name" />
          </label>
          <label>Mfg date
            <Input type="date" value={String(data.ship_mfg_date ?? e.mfg_date ?? "").slice(0, 10)} disabled={readOnly} onChange={(ev) => setMany({ ship_mfg_date: ev.target.value })} className={`${field} mt-1`} data-testid="ship-mfg-date" />
          </label>
          <label>Packed by
            <Input value={data.ship_packed_by ?? ""} placeholder={e.packed_by} disabled={readOnly} onChange={(ev) => setMany({ ship_packed_by: ev.target.value })} className={`${field} mt-1`} data-testid="ship-packed-by" />
          </label>
          <label>Checked by
            <Input value={data.ship_checked_by ?? ""} placeholder={e.checked_by} disabled={readOnly} onChange={(ev) => setMany({ ship_checked_by: ev.target.value })} className={`${field} mt-1`} data-testid="ship-checked-by" />
          </label>
        </div>

        <div className="border border-neutral-200 rounded-sm">
          <div className="flex flex-wrap items-center gap-3 px-3 py-2 border-b border-neutral-200 bg-neutral-50 text-xs">
            <span className="font-semibold text-neutral-700">Boxes</span>
            {!readOnly && (
              <>
                <span className="text-neutral-500">Same gross weight for every box</span>
                <Input type="number" step="0.001" min="0" value={allGross} onChange={(ev) => setAllGross(ev.target.value)} placeholder="kg" className="h-7 w-24 text-xs" data-testid="ship-all-gross" aria-label="Gross weight for every box" />
                <button type="button" onClick={() => applyGross(allGross)} disabled={!has(allGross)} className="text-[#FF5A00] hover:underline disabled:text-neutral-400 disabled:no-underline" data-testid="ship-apply-gross">Apply to all</button>
                {qtyHintsLeft > 0 && <button type="button" onClick={useQtyHints} className="text-sky-700 hover:underline" data-testid="ship-use-qty-hints">Use the {qtyHintsLeft} suggested quantit{qtyHintsLeft === 1 ? "y" : "ies"}</button>}
                {hintsLeft > 0 && <button type="button" onClick={useHints} className="text-sky-700 hover:underline" data-testid="ship-use-hints">Use the {hintsLeft} weight{hintsLeft === 1 ? "" : "s"} written in the dispatch register</button>}
              </>
            )}
          </div>
          <div className="max-h-72 overflow-auto">
            <table className="w-full text-xs">
              <thead className="text-neutral-500"><tr><th className="text-left px-3 py-1.5 w-20">Box</th><th className="text-left px-2 py-1.5">Qty per box</th><th className="text-left px-2 py-1.5 w-44">Gross weight (kg)</th></tr></thead>
              <tbody>
                {e.boxes.map((b, i) => (
                  <tr key={b.no} className="border-t border-neutral-100" data-testid={`ship-box-${i}`}>
                    <td className="px-3 py-1 font-mono-num">{b.no}/{e.boxes.length}</td>
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-1.5">
                        <Input value={(Array.isArray(data.ship_boxes) && data.ship_boxes[i]?.qty) || ""} disabled={readOnly}
                          placeholder={b.auto_qty || (b.qty_hint != null ? `${qtyOf(b)} (suggested)` : "e.g. 5250 Labels (1750 X 03)")}
                          onChange={(ev) => setBox(i, { qty: ev.target.value })} className="h-7 text-xs" data-testid={`ship-box-${i}-qty`} aria-label={`Qty per box ${b.no}`} />
                        {!b.qty_text && b.qty_hint != null && !readOnly && <button type="button" onClick={() => setBox(i, { qty: qtyOf(b) })} className="shrink-0 text-[10px] text-sky-700 hover:underline" title="An even share of what was packed">use {plain(b.qty_hint)}</button>}
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-1.5">
                        <Input type="number" step="0.001" min="0" value={(Array.isArray(data.ship_boxes) && data.ship_boxes[i]?.gross) || ""} disabled={readOnly}
                          placeholder={b.gross || (b.hint != null ? plain(b.hint) : "kg")} onChange={(ev) => setBox(i, { gross: ev.target.value })} className="h-7 w-24 text-xs" data-testid={`ship-box-${i}-gross`} aria-label={`Gross weight of box ${b.no}`} />
                        {!b.gross && b.hint != null && !readOnly && <button type="button" onClick={() => setBox(i, { gross: plain(b.hint) })} className="text-[10px] text-sky-700 hover:underline" title="The dispatch register says this">use {plain(b.hint)}</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!readOnly && (
            <div className="flex items-center gap-4 px-3 py-1.5 border-t border-neutral-100 text-xs">
              <button type="button" onClick={addBox} className="inline-flex items-center gap-1 text-[#FF5A00] hover:underline" data-testid="ship-add-box"><Plus size={12} /> Add a box</button>
              {extra && <button type="button" onClick={removeBox} className="inline-flex items-center gap-1 text-neutral-500 hover:underline"><Trash2 size={12} /> Remove the last box</button>}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <SizePicker value={shipSize} onChange={setShipSize} testId="ship-size" />
          <RangePicker total={e.boxes.length} value={shipRange} onChange={setShipRange} noun="box" />
          <Button type="button" onClick={() => start("shipper", ship, boxSet)} data-testid="ship-print" className="h-8 rounded-sm bg-[#FF5A00] hover:bg-[#e65100] text-white">
            <Printer size={14} className="mr-1.5" /> Print {boxSet.length} shipper label{boxSet.length === 1 ? "" : "s"}
          </Button>
        </div>
        {boxSet.length > 0 && (
          <div className="overflow-auto border border-dashed border-neutral-300 bg-neutral-50 p-2" data-testid="ship-preview">
            <div style={{ zoom: 0.62, width: "fit-content" }}><ShipperLabel e={e} box={boxSet[0]} total={e.boxes.length} qr={qr} w={ship.w} h={ship.h} /></div>
          </div>
        )}
        <div className="text-[11px] text-neutral-500">The QR under each shipper label opens this job, with what every stage before Dispatch recorded.</div>
      </section>

      {run && (
        <div ref={printRef} style={{ position: "fixed", left: "-10000px", top: 0 }} aria-hidden="true">
          {run.kind === "core"
            ? run.items.map((it, i) => <CoreLabel key={i} e={e} labels={it.labels} rollNo={it.rollNo} totalRolls={it.totalRolls} w={run.size.w} h={run.size.h} qr={qr} />)
            : run.items.map((b) => <ShipperLabel key={b.no} e={e} box={b} total={e.boxes.length} qr={qr} w={run.size.w} h={run.size.h} />)}
        </div>
      )}
    </div>
  );
}
